/**
 * Adaptive Biases
 * ---------------------------------------------------------------
 * Runtime translator between persisted `generation_biases` rows
 * (committed via the Adaptive Learning Engine) and the synthesis
 * pipeline used by Generate Intelligence Batch.
 *
 * Pure: no React, no UI. Only fetch + apply.
 */

import { supabase } from "@/integrations/supabase/client";
import type { ArchetypeKey } from "@/lib/simulationEngine";

export interface ActiveBias {
  id: string;
  dimension: string;       // archetype | format | escalation | pacing | hook
  key: string;             // e.g. "withdrawal", "pov", "guilt"
  bias_direction: "increase" | "reduce";
  weight: number;          // 0–1 normalized influence
  confidence: number;      // 0–100
  impact_score: number;    // 0–100 — derived perf delta
  rationale: string | null;
  cycle_id: string | null;
}

/**
 * Fetch the currently-active intelligence directives. The
 * Adaptive Learning Engine deactivates older rows on each commit,
 * so this returns only the latest cycle's biases.
 */
export async function fetchActiveBiases(): Promise<ActiveBias[]> {
  const { data, error } = await (supabase as any)
    .from("generation_biases")
    .select("id, dimension, key, bias_direction, weight, confidence, impact_score, rationale, cycle_id")
    .eq("active", true)
    .order("confidence", { ascending: false })
    .limit(40);
  if (error) {
    console.error("[adaptiveBiases] fetch failed", error);
    return [];
  }
  return (data ?? []) as ActiveBias[];
}

/**
 * Build per-archetype sampling weights derived from active biases.
 * Default weight = 1.0; "increase" lifts; "reduce" attenuates.
 * Magnitude is scaled by confidence × stored weight.
 */
export function buildArchetypeWeights(
  biases: ActiveBias[],
  pool: ArchetypeKey[]
): Record<string, number> {
  const w: Record<string, number> = {};
  pool.forEach((k) => { w[k] = 1; });

  biases
    .filter((b) => b.dimension === "archetype")
    .forEach((b) => {
      const k = b.key;
      if (w[k] === undefined) return;
      const magnitude = Math.max(0.1, Math.min(1.5, (b.confidence / 100) * (b.weight || 1)));
      if (b.bias_direction === "increase") w[k] = Math.min(3, 1 + magnitude);
      else                                  w[k] = Math.max(0.2, 1 - magnitude);
    });
  return w;
}

/** Deterministic-ish weighted shuffle: items with higher weight surface earlier. */
export function weightedShuffle<T>(items: T[], weight: (t: T) => number): T[] {
  return items
    .map((t) => ({ t, score: Math.pow(Math.random(), 1 / Math.max(0.0001, weight(t))) }))
    .sort((a, b) => b.score - a.score)
    .map((x) => x.t);
}

/**
 * Format / escalation / pacing biases — return a list of
 * "preferred" keys ordered by confidence, plus a list of
 * "suppressed" keys to avoid when possible.
 */
export function biasPreferences(biases: ActiveBias[], dimension: string) {
  const prefer = biases
    .filter((b) => b.dimension === dimension && b.bias_direction === "increase")
    .sort((a, b) => b.confidence - a.confidence)
    .map((b) => b.key);
  const suppress = biases
    .filter((b) => b.dimension === dimension && b.bias_direction === "reduce")
    .sort((a, b) => b.confidence - a.confidence)
    .map((b) => b.key);
  return { prefer, suppress };
}

/** Produce a 1-line operator-readable summary of the active set. */
export function summarizeBiases(biases: ActiveBias[]): string {
  if (biases.length === 0) return "No active intelligence directives";
  const inc = biases.filter((b) => b.bias_direction === "increase").length;
  const red = biases.filter((b) => b.bias_direction === "reduce").length;
  return `${biases.length} active directives · ${inc} amplify · ${red} suppress`;
}
